import * as THREE from 'three/webgpu';
import type { ImportedModelTopologySelectionInput } from '../../../types';
export type { ImportedModelTopologySelectionInput } from '../../../types';
import { setRaycasterFirstHitOnly } from '../../raycast-acceleration/setRaycasterFirstHitOnly';
import type { Viewer } from '../../viewer/Viewer';
import { resolveImportedModelBodyTopologyRecord } from '../topology/imported-model-topology-runtime';
import { ImportedModelRaycastResolver } from './ImportedModelRaycastResolver';
import {
  ImportedModelTopologyPickResolver,
  isImportedTopologySelectionObject,
} from './ImportedModelTopologyPickResolver';
import { resolveModelBodySceneRegistry } from '../../model-body/model-body-scene-registry-runtime';

/**
 * 导入模型拓扑拾取服务选项。
 */
export interface ImportedModelTopologySelectionServiceOptions {
  /** 当前 Viewer。 */
  renderer: Viewer;
  /** imported-model 第一阶段射线对象解析器。 */
  importedModelRaycastResolver: ImportedModelRaycastResolver;
}

/**
 * imported-model 拓扑选择服务。
 *
 * 设计说明：
 * 1. 第一阶段只从可见 imported mesh 定位 body，保持与装配体选择一致；
 * 2. 第二阶段再委托 `ImportedModelTopologyPickResolver` 解析 FeatureLine / FeatureFace / FeaturePoint；
 * 3. 该服务不绑定具体选择处理器，让 Sketch 与 Assembly 可以复用同一条模型拓扑拾取链。
 */
export class ImportedModelTopologySelectionService {
  /** 当前 Viewer。 */
  private readonly renderer: Viewer;

  /** imported-model 第一阶段射线对象解析器。 */
  private readonly importedModelRaycastResolver: ImportedModelRaycastResolver;

  /** imported-model 第二阶段拓扑拾取解析器。 */
  private readonly importedModelTopologyPickResolver = new ImportedModelTopologyPickResolver();

  /** 服务内部独立射线，避免污染调用方选择器的通用射线状态。 */
  private readonly raycaster = setRaycasterFirstHitOnly(new THREE.Raycaster());

  /** 服务内部复用 NDC 坐标，避免高频 hover 拾取反复创建临时向量。 */
  private readonly mouseNdc = new THREE.Vector2();

  /** 第一阶段 Imported 候选复用缓冲；调用方数组保持只读。 */
  private readonly importedTargetBuffer: THREE.Object3D[] = [];

  /**
   * 构造函数。
   *
   * @param options 服务依赖。
   */
  public constructor(options: ImportedModelTopologySelectionServiceOptions) {
    this.renderer = options.renderer;
    this.importedModelRaycastResolver = options.importedModelRaycastResolver;
  }

  /**
   * 释放服务拥有的派生拾取缓存，不依赖所借用模型先被销毁。
   *
   * 不释放注入的 Viewer、第一阶段解析器、模型几何或材质；重复调用保持幂等。
   */
  public dispose(): void {
    this.importedModelTopologyPickResolver.clear();
    this.importedTargetBuffer.length = 0;
  }

  /**
   * 收集 imported-model 第一阶段射线候选。
   *
   * @returns 可参与 imported-model 拾取的对象集合。
   */
  public collectRaycastTargets(): readonly THREE.Object3D[] {
    return this.importedModelRaycastResolver.getRaycastTargets();
  }

  /**
   * 拾取导入模型对象或拓扑代理。
   *
   * @param input 拾取输入。
   * @returns imported-model 命中；未命中时返回 `null`。
   */
  public pickAt(input: ImportedModelTopologySelectionInput): THREE.Intersection<THREE.Object3D> | null {
    const container = this.renderer.container;
    const camera = this.renderer.camera;
    if (!container || !camera) {
      return null;
    }

    let hit: THREE.Intersection<THREE.Object3D> | null;
    try {
      hit = this.pickRawImportedModelAt(input);
    } finally {
      // 数组属于服务，候选对象仅借用到本次同步查询结束；空闲和失败时不延长旧模型生命周期。
      this.importedTargetBuffer.length = 0;
    }
    if (!hit) {
      return null;
    }

    /**
     * 对象模式不能再命中已由特征预览结果替换的源实体；拓扑模式仍复用同一真实宿主继续吸纳引用。
     * 判定规则位于统一实体注册表，本服务只负责把 imported 二阶段拾取接入该通用生命周期。
     */
    if (
      input.selectionMode === 'object'
      && resolveModelBodySceneRegistry(this.renderer.scene)
        .isPreviewReplacementSourceObject(hit.object)
    ) {
      return null;
    }

    const importedSelectionObject = this.importedModelRaycastResolver.resolveSelectionObject(hit.object);
    if (!importedSelectionObject) {
      return null;
    }

    const bodyTopologyRecord = resolveImportedModelBodyTopologyRecord(hit);
    if (!bodyTopologyRecord && !isImportedTopologySelectionObject(hit.object)) {
      /**
       * 没有 body 拓扑记录时仍保留原 imported-model 对象选择语义，
       * 这样默认装配体选择不会因为缺少 topo sidecar 而失效。
       */
      return {
        ...hit,
        object: importedSelectionObject,
      };
    }

    const topologyPick = this.importedModelTopologyPickResolver.resolve({
      hostObject: importedSelectionObject,
      rawHit: hit,
      selectionMode: input.selectionMode,
      allowOccludedSelection: input.allowOccludedSelection,
      camera,
      containerRect: container.getBoundingClientRect(),
      clientX: input.clientX,
      clientY: input.clientY,
      ray: this.raycaster.ray,
    });

    return topologyPick && (this.renderer.isSectionPointVisible?.(topologyPick.point) ?? true)
      ? topologyPick
      : null;
  }

  /**
   * 直接拾取 imported-model 的原始 three.object。
   *
   * @param input 拾取输入。
   * @returns 原始导入模型命中；未命中时返回 `null`。
   */
  private pickRawImportedModelAt(
    input: ImportedModelTopologySelectionInput,
  ): THREE.Intersection<THREE.Object3D> | null {
    const container = this.renderer.container;
    const camera = this.renderer.camera;
    if (!container || !camera) {
      return null;
    }

    const rect = container.getBoundingClientRect();
    this.mouseNdc.x = ((input.clientX - rect.left) / rect.width) * 2 - 1;
    this.mouseNdc.y = -((input.clientY - rect.top) / rect.height) * 2 + 1;
    this.raycaster.setFromCamera(this.mouseNdc, camera);

    const importedTargets = this.importedTargetBuffer;
    importedTargets.length = 0;
    for (const object of input.interactableObjects) {
      if (this.importedModelRaycastResolver.isImportedModelObject(object)) {
        importedTargets.push(object);
      }
    }
    if (importedTargets.length === 0) {
      return null;
    }

    const previousFirstHitOnly = Reflect.get(this.raycaster, 'firstHitOnly') === true;
    // 剖面启用时最近的几何命中可能已经被 GPU 裁掉，必须继续收集后方可见命中。
    setRaycasterFirstHitOnly(this.raycaster, !(this.renderer.hasActiveSectionView?.() ?? false));
    let firstVisibleHit: THREE.Intersection<THREE.Object3D> | null = null;
    let firstVisibleTopologyHit: THREE.Intersection<THREE.Object3D> | null = null;
    try {
      // 完整求交成本决定普通网格是否粗筛；原实例槽索引和剖面精确配置保持不变。
      const rawHits = this.renderer.intersectMeshRaycastCandidates
        ? this.renderer.intersectMeshRaycastCandidates(importedTargets, this.raycaster, this, 'imported')
        : this.raycaster.intersectObjects(importedTargets, false);
      /**
       * 实例命中归一、剖面过滤和拓扑代理优先级保持原距离顺序一次完成，避免每次 Hover
       * 分配 map/filter 中间数组。找到第一个可见拓扑代理后即可结束，因为后续代理更远。
       */
      for (const rawHit of rawHits) {
        const resolvedHit = this.importedModelRaycastResolver.resolveInstancedHit(rawHit);
        if (!resolvedHit || !(this.renderer.isSectionPointVisible?.(resolvedHit.point) ?? true)) {
          continue;
        }
        firstVisibleHit ??= resolvedHit;
        if (isImportedTopologySelectionObject(resolvedHit.object)) {
          firstVisibleTopologyHit = resolvedHit;
          break;
        }
      }
    } finally {
      setRaycasterFirstHitOnly(this.raycaster, previousFirstHitOnly);
    }

    /**
     * 若候选里已经存在 imported topology 代理，优先沿用代理对象；
     * 否则回退到第一条 visible mesh 命中，让第二阶段解析继续定位 body。
     */
    return firstVisibleTopologyHit ?? firstVisibleHit;
  }
}
